/**
 * Plain-English explanation of an audit event, for the Audit Log detail view.
 * Maps the operation taxonomy to a one-line sentence a non-specialist can read.
 * Pure presentation — no PII beyond what's already on the event.
 */
export interface Explainable {
  source: string;
  event_type: string;
  actor?: string | null;
  target?: string | null;
  category?: string | null;
  metadata?: Record<string, unknown> | null;
}

function who(e: Explainable): string {
  return e.actor && e.actor.trim() ? e.actor : 'An unknown actor';
}
function onTarget(e: Explainable): string {
  return e.target && e.target.trim() ? ` (${e.target})` : '';
}

type Rule = { test: RegExp; say: (e: Explainable) => string };

const M365_RULES: Rule[] = [
  { test: /^Add-MailboxPermission/i, say: (e) => `${who(e)} granted another account access to a mailbox${onTarget(e)} — a delegate can now read that mailbox.` },
  { test: /^Add-RecipientPermission/i, say: (e) => `${who(e)} granted Send-As on a mailbox${onTarget(e)} — they can send mail as that account.` },
  { test: /^Remove-MailboxPermission/i, say: (e) => `${who(e)} removed mailbox access${onTarget(e)}.` },
  { test: /InboxRule/i, say: (e) => `${who(e)} created or changed an inbox rule${onTarget(e)} — inbox rules can silently forward or move mail, so worth a look.` },
  { test: /TransportRule/i, say: (e) => `${who(e)} changed a mail-flow (transport) rule — these affect routing for the whole organisation.` },
  { test: /^New-Mailbox/i, say: (e) => `${who(e)} created a mailbox${onTarget(e)}.` },
  { test: /^(Remove|Disable)-Mailbox/i, say: (e) => `${who(e)} removed or disabled a mailbox${onTarget(e)}.` },
  { test: /member to role/i, say: (e) => `${who(e)} added a member to an administrative role — that account now has elevated privileges.` },
  { test: /Consent to application/i, say: (e) => `${who(e)} consented an application to access data — review what permissions it was granted.` },
  { test: /Certificates and secrets|service principal credentials|credentials/i, say: (e) => `${who(e)} added or changed an application's credentials (a client secret or certificate)${onTarget(e)}.` },
  { test: /app role assignment/i, say: (e) => `${who(e)} granted an application an API permission (app role) — the app can now call that API.` },
  { test: /delegated permission grant/i, say: (e) => `${who(e)} granted a delegated OAuth permission to an application.` },
  { test: /^Add application|^Update application/i, say: (e) => `${who(e)} registered or updated an app registration${onTarget(e)}.` },
  { test: /service principal/i, say: (e) => `${who(e)} added or updated an enterprise application / service principal${onTarget(e)}.` },
  { test: /password/i, say: (e) => `${who(e)} reset or changed a user's password${onTarget(e)}.` },
  { test: /^Update user/i, say: (e) => `${who(e)} changed a user's directory attributes${onTarget(e)}.` },
  { test: /^Update device/i, say: (e) => `${who(e)} changed a device object in the directory.` },
  { test: /Anonymous/i, say: (e) => `${who(e)} created an anonymous "anyone with the link" share${onTarget(e)} — accessible without signing in.` },
  { test: /CompanyLink/i, say: (e) => `${who(e)} created an organisation-wide share link${onTarget(e)}.` },
  { test: /SecureLink/i, say: (e) => `${who(e)} created or added someone to a sign-in-required share link${onTarget(e)}.` },
  { test: /Sharing(Set|Invitation)/i, say: (e) => `${who(e)} shared a file or folder with someone${onTarget(e)}.` },
  { test: /(Added|Add member) ?to ?group/i, say: (e) => `${who(e)} added a member to a group${onTarget(e)}.` },
  { test: /Export|PurviewSearch.*Export|ViewedSearchExported/i, say: (e) => `${who(e)} exported eDiscovery/search results — content left the compliance boundary.` },
  { test: /^Search|PurviewSearch|ReviewSet/i, say: (e) => `${who(e)} ran a content or eDiscovery search.` },
  { test: /MailItemsAccessed/i, say: (e) => `${who(e)} accessed another user's mailbox items (non-owner access)${onTarget(e)}.` },
  { test: /Dlp/i, say: (e) => `Content matched a DLP policy — sensitive data was detected${onTarget(e)}.` },
  { test: /MIPLabel|sensitivity/i, say: (e) => `${who(e)} acted on sensitivity-labelled content${onTarget(e)}.` },
];

const EGNYTE_RULES: Rule[] = [
  { test: /DOWNLOAD|EXPORT/i, say: (e) => `${who(e)} downloaded content from Egnyte${onTarget(e)}.` },
  { test: /DELETE|RECYCLE/i, say: (e) => `${who(e)} deleted content in Egnyte${onTarget(e)}.` },
  { test: /MOVE|COPY|RENAME|CREATE|UPLOAD|OVERWRITE/i, say: (e) => `${who(e)} changed files/folders in Egnyte${onTarget(e)}.` },
  { test: /PUBLIC|ANONYMOUS|EXTERNAL|EVERYONE/i, say: (e) => `${who(e)} created an external/public share in Egnyte${onTarget(e)} — accessible outside the organisation.` },
  { test: /PERMISSION|SHARE|LINK|GROUP|MEMBER|ROLE|OWNER|FOLDER/i, say: (e) => `${who(e)} changed sharing or permissions in Egnyte${onTarget(e)}.` },
  { test: /LOGIN|AUTH/i, say: (e) => `A sign-in attempt to Egnyte ${e.actor ? `by ${e.actor} ` : ''}failed.` },
];

/** Return a one-line plain-English explanation of an audit event. */
export function explainAuditEvent(e: Explainable): string {
  const rules = e.source === 'egnyte_audit' ? EGNYTE_RULES : M365_RULES;
  const op = (e.event_type ?? '').trim();
  for (const r of rules) if (r.test.test(op)) return r.say(e);
  const what = e.category === 'sensitive_read' ? 'A sensitive-read event' : 'A change';
  return `${what} — "${op}"${onTarget(e)}${e.actor ? ` by ${e.actor}` : ''}.`;
}
